import { useState } from 'react'
import type {
  DownstreamMembers,
  GeoJSONFeatureCollection,
  GeoJSONPoint,
} from '../../types/topology.ts'
import { friendlyError } from '../../utils/friendlyError.ts'

interface DownstreamButtonProps {
  label: string
  fetcher: () => Promise<DownstreamMembers>
  onShowDownstream: (fc: GeoJSONFeatureCollection<GeoJSONPoint>) => void
}

/**
 * Shared "show everything fed by …" button for the transformer and device detail
 * panels (spec §4.3 map highlight). Degraded or empty data is always explicit,
 * never a silent no-op (spec §9):
 *  - basis 'unavailable' (device with no completed protection run) → notice, no highlight
 *  - real basis but zero geometry-bearing members → "no members with map locations"
 *    notice, no highlight (an empty collection would be a misleading no-op)
 *  - real members → count line + drives the map highlight
 * Callers key this by the selected feature id so a feature switch resets state
 * instead of briefly showing the prior feature's result.
 */
export function DownstreamButton({ label, fetcher, onShowDownstream }: DownstreamButtonProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [count, setCount] = useState<number | null>(null)
  const [notice, setNotice] = useState<'unavailable' | 'empty' | null>(null)

  async function handleClick() {
    setLoading(true)
    setError(null)
    setNotice(null)
    setCount(null)
    try {
      const res = await fetcher()
      // No completed protection run is NOT the same as a device that feeds nothing.
      if (res.basis === 'unavailable') {
        setNotice('unavailable')
        return
      }
      // A real basis with zero mappable members must say so, not highlight an empty
      // collection nor report a misleading "0 highlighted".
      if (res.features.features.length === 0) {
        setNotice('empty')
        return
      }
      setCount(res.member_count)
      onShowDownstream(res.features)
    } catch (err) {
      setError(friendlyError(err, 'load downstream meters'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="mb-3">
      <button
        onClick={() => handleClick()}
        disabled={loading}
        className="w-full px-3 py-1.5 text-sm bg-amber-50 text-amber-700 border border-amber-200 rounded hover:bg-amber-100 disabled:opacity-50"
      >
        {loading ? 'Loading…' : label}
      </button>
      {notice === 'unavailable' && (
        <p className="text-xs text-amber-600 mt-1">No protection run — downstream unavailable</p>
      )}
      {notice === 'empty' && (
        <p className="text-xs text-amber-600 mt-1">No downstream members with map locations</p>
      )}
      {count != null && (
        <p className="text-xs text-gray-500 mt-1">{count} downstream member(s) highlighted</p>
      )}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  )
}
